export type DeltaDirection = "higher_is_better" | "higher_is_worse" | "neutral";

export interface DeltaResult {
  pct: number | null;
  /** Raw text override for extreme cases (e.g. "↑ >500%"). null = use pct. */
  label: string | null;
  /** Visual direction: "up" | "down" | "flat" */
  trend: "up" | "down" | "flat";
  /** Whether trend is semantically good, given direction config. */
  isGood: boolean | null;
}

export interface ComputeDeltaOptions {
  direction: DeltaDirection;
  /** Minimum absolute base value to consider the % meaningful. Default: 100 */
  minBase?: number;
}

/**
 * Computes a period-over-period delta with 5 edge-case guards:
 *  1. base = 0           → label "—", trend "flat"
 *  2. base < minBase     → label "—" (base too small to be meaningful)
 *  3. |pct| > 500%       → label ">500%" (sem seta: quem exibe já desenha o
 *                          ícone de direção a partir de `trend`; embutir a seta
 *                          aqui duplicava o símbolo no card)
 *  4. current or base is null → label "—"
 *  5. |pct| < 0.5%       → label "—" (noise)
 */
export function computeDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
  options: ComputeDeltaOptions,
): DeltaResult {
  const { direction, minBase = 100 } = options;

  const flat: DeltaResult = { pct: null, label: "—", trend: "flat", isGood: null };

  if (current == null || previous == null) return flat;
  if (previous === 0) return flat;
  if (Math.abs(previous) < minBase) return flat;

  const pct = ((current - previous) / Math.abs(previous)) * 100;

  if (Math.abs(pct) < 0.5) return flat;

  const trend: "up" | "down" = pct > 0 ? "up" : "down";

  if (Math.abs(pct) > 500) {
    const isGood = direction === "neutral" ? null
      : direction === "higher_is_better" ? pct > 0
      : pct < 0;
    return { pct: null, label: ">500%", trend, isGood };
  }

  const isGood = direction === "neutral" ? null
    : direction === "higher_is_better" ? pct > 0
    : pct < 0;

  return { pct, label: null, trend, isGood };
}
